/**
 * Staff roles and the route groups from the admin access map
 * (business/strategy/build-2026-10/admin-audit-parts/05-admin-access-map.md).
 *
 * Client-side gating only hides and blocks UI. The database (RLS + RPC role
 * checks, owned by the backend streams) is what actually enforces access.
 */

export const ROLE = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  CONTENT_MANAGER: 3,
  SUPPORT_MANAGER: 4,
  USER: 5,
} as const;

export type RoleId = (typeof ROLE)[keyof typeof ROLE];

export const ROLE_LABEL: Record<number, string> = {
  1: "Super Admin",
  2: "Admin",
  3: "Content Manager",
  4: "Support Manager",
  5: "Reader",
};

/** Finance, system settings, staff-role definitions. */
export const ROLES_SUPER: readonly number[] = [1];
/** Readers (users), plans, payment methods, currencies, collaborators, referrals, destructive reader actions. */
export const ROLES_ADMIN: readonly number[] = [1, 2];
/** Content, notifications, AI, promotions, import, book editing and content deletes. */
export const ROLES_CONTENT: readonly number[] = [1, 2, 3];
/** Every staff member: Today, profile, Mizo strings, read-only Money pages. */
export const ROLES_STAFF: readonly number[] = [1, 2, 3, 4];

export const STAFF_ROLE_IDS = ROLES_STAFF;

type HasRoles = { roleIds?: number[] } | null | undefined;

export const hasAnyRole = (user: HasRoles, roles: readonly number[]): boolean => {
  if (!user?.roleIds?.length) return false;
  return user.roleIds.some((id) => roles.includes(id));
};

/** A staff member holds at least one of roles 1-4. Zero roles, or only role 5, is not staff. */
export const isStaff = (user: HasRoles): boolean => hasAnyRole(user, ROLES_STAFF);

/** Highest-privilege role label for display ("Super Admin · full access"). */
export const primaryRoleLabel = (user: HasRoles): string => {
  const ids = [...(user?.roleIds ?? [])].filter((id) => id >= 1 && id <= 4).sort();
  if (!ids.length) return "No staff role";
  return ROLE_LABEL[ids[0]] ?? `Role ${ids[0]}`;
};

export const roleScopeLabel = (user: HasRoles): string => {
  if (hasAnyRole(user, [1])) return "full access";
  if (hasAnyRole(user, [2])) return "admin access";
  if (hasAnyRole(user, [3])) return "content access";
  if (hasAnyRole(user, [4])) return "support access";
  return "no access";
};
